import React, { useEffect, useRef, useState } from "react";
import { ArrowUp, ArrowDown, ArrowLeft, ArrowRight } from "lucide-react";
import { ABtn, AScore, AResult, PadButton } from "./arcadeUi";
import { sfx } from "@/lib/soundfx";

// Pac-Man-style maze. 0=wall, 1=dot, 2=empty
const MAP = [
  "###########",
  "#....#....#",
  "#.##.#.##.#",
  "#.........#",
  "#.##.#.##.#",
  "#..#.P.#..#",
  "#.##.#.##.#",
  "#....#....#",
  "###########",
];
const CELL = 20, COLS = MAP[0].length, ROWS = MAP.length, W = COLS * CELL, H = ROWS * CELL;

export default function MazeMuncher() {
  const canvasRef = useRef(null);
  const g = useRef({});
  const dir = useRef({ x: 0, y: 0 });
  const next = useRef({ x: 0, y: 0 });
  const scoreRef = useRef(0);
  const [score, setScore] = useState(0);
  const [status, setStatus] = useState("idle");

  const buildDots = () => {
    const dots = [];
    for (let r = 0; r < ROWS; r++) for (let c = 0; c < COLS; c++) if (MAP[r][c] === ".") dots.push(`${c},${r}`);
    return new Set(dots);
  };
  const wall = (c, r) => c < 0 || c >= COLS || r < 0 || r >= ROWS || MAP[r][c] === "#";
  const start = () => {
    g.current = { px: 5, py: 5, ghosts: [{ x: 1, y: 1, dx: 1, dy: 0 }, { x: 9, y: 7, dx: -1, dy: 0 }], dots: buildDots(), tick: 0, mouth: 0 };
    dir.current = { x: 0, y: 0 }; next.current = { x: 0, y: 0 };
    scoreRef.current = 0; setScore(0); setStatus("playing"); sfx.beep();
  };
  const setD = (x, y) => { next.current = { x, y }; };

  useEffect(() => {
    const onKey = (e) => {
      if (["ArrowUp", "w"].includes(e.key)) setD(0, -1);
      else if (["ArrowDown", "s"].includes(e.key)) setD(0, 1);
      else if (["ArrowLeft", "a"].includes(e.key)) setD(-1, 0);
      else if (["ArrowRight", "d"].includes(e.key)) setD(1, 0);
      else return;
      e.preventDefault();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    const ctx = canvasRef.current.getContext("2d");
    const draw = () => {
      ctx.fillStyle = "#0a0a12"; ctx.fillRect(0, 0, W, H);
      for (let r = 0; r < ROWS; r++) for (let c = 0; c < COLS; c++) { if (MAP[r][c] === "#") { ctx.fillStyle = "#3b3ba0"; ctx.fillRect(c * CELL + 1, r * CELL + 1, CELL - 2, CELL - 2); } }
      const s = g.current;
      ctx.fillStyle = "#e8b04b"; (s.dots || new Set()).forEach((k) => { const [c, r] = k.split(",").map(Number); ctx.beginPath(); ctx.arc(c * CELL + CELL / 2, r * CELL + CELL / 2, 2.5, 0, 7); ctx.fill(); });
      ctx.fillStyle = "#ffd400"; ctx.beginPath(); ctx.arc((s.px || 0) * CELL + CELL / 2, (s.py || 0) * CELL + CELL / 2, CELL / 2 - 2, 0.2, 6.08); ctx.lineTo((s.px || 0) * CELL + CELL / 2, (s.py || 0) * CELL + CELL / 2); ctx.fill();
      (s.ghosts || []).forEach((gh, i) => { ctx.fillStyle = ["#ef4444", "#22d3ee"][i]; ctx.fillRect(gh.x * CELL + 3, gh.y * CELL + 3, CELL - 6, CELL - 6); });
    };
    if (status !== "playing") { draw(); return; }
    let raf, acc = 0, last = performance.now();
    const INT = 150;
    const loop = (now) => {
      raf = requestAnimationFrame(loop);
      acc += now - last; last = now;
      while (acc >= INT) {
        acc -= INT;
        const s = g.current;
        if (!wall(s.px + next.current.x, s.py + next.current.y) && (next.current.x || next.current.y)) dir.current = next.current;
        if (!wall(s.px + dir.current.x, s.py + dir.current.y)) { s.px += dir.current.x; s.py += dir.current.y; }
        const key = `${s.px},${s.py}`;
        if (s.dots.has(key)) { s.dots.delete(key); scoreRef.current += 10; setScore(scoreRef.current); sfx.eat(); }
        // ghosts
        for (const gh of s.ghosts) {
          const opts = [[1, 0], [-1, 0], [0, 1], [0, -1]].filter(([dx, dy]) => !wall(gh.x + dx, gh.y + dy) && !(dx === -gh.dx && dy === -gh.dy));
          const list = opts.length ? opts : [[-gh.dx, -gh.dy]];
          // bias toward player
          list.sort((a, b) => (Math.abs(gh.x + a[0] - s.px) + Math.abs(gh.y + a[1] - s.py)) - (Math.abs(gh.x + b[0] - s.px) + Math.abs(gh.y + b[1] - s.py)));
          const pick = Math.random() < 0.7 ? list[0] : list[(Math.random() * list.length) | 0];
          gh.dx = pick[0]; gh.dy = pick[1]; gh.x += gh.dx; gh.y += gh.dy;
        }
        if (s.ghosts.some((gh) => gh.x === s.px && gh.y === s.py)) { cancelAnimationFrame(raf); sfx.explode(); setStatus("over"); return; }
        if (s.dots.size === 0) { cancelAnimationFrame(raf); sfx.win(); setStatus("win"); return; }
      }
      draw();
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [status]);

  return (
    <div className="flex flex-col items-center">
      <div className="mb-2"><AScore label="Score" value={score} /></div>
      <div className="relative">
        <canvas ref={canvasRef} width={W} height={H} className="w-full max-w-[240px] rounded-md border-[3px] border-kirana-brown" style={{ imageRendering: "pixelated", touchAction: "none" }} />
        {status !== "playing" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center rounded-md bg-black/60 p-2">
            {status === "over" && <AResult win={false} text={`Caught! · ${score}`} testId="maze-over" />}
            {status === "win" && <AResult win={true} text={`All clear! · ${score}`} testId="maze-win" />}
            <ABtn tone="green" onClick={start} className="mt-3">{status === "idle" ? "Start" : "Play Again"}</ABtn>
          </div>
        )}
      </div>
      <div className="mt-3 grid w-max grid-cols-3 gap-1.5">
        <span /><PadButton testId="maze-up" onPress={() => setD(0, -1)}><ArrowUp className="h-5 w-5" /></PadButton><span />
        <PadButton testId="maze-left" onPress={() => setD(-1, 0)}><ArrowLeft className="h-5 w-5" /></PadButton>
        <span />
        <PadButton testId="maze-right" onPress={() => setD(1, 0)}><ArrowRight className="h-5 w-5" /></PadButton>
        <span /><PadButton testId="maze-down" onPress={() => setD(0, 1)}><ArrowDown className="h-5 w-5" /></PadButton><span />
      </div>
    </div>
  );
}
